import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatTimestamp } from '../../utils/TextUtils.js';
import ConfirmDialog from "../../components/ConfirmDialog.js";
import Notification from "../../components/Notification.js";

function Users() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isExpiringUsers, setIsExpiringUsers] = useState(false);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const userRole = localStorage.getItem("userRole");
    const token = localStorage.getItem("access_token");
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [expiryPreview, setExpiryPreview] = useState(null); // What expiring the accounts would do
    // "Last Login" is shown to admins and super admins only (the API sends it only to them).
    const showLastLogin = allowedToView(userRole, ['admin', 'superadmin']);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const isAdmin = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']));
    };
    const canDeactivateAllAccounts = (userRole) => {
        return (allowedToView(userRole, ['superadmin']));
    };
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 15,
        total: 0,
        lastPage: 1,
        isPaginated: true
    });

    const navigate = useNavigate();
    const { status } = useParams();
    const location = useLocation();

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null); // Close dropdown if the same one is clicked
        } else {
            setIsDropdownOpen(index); // Open the clicked dropdown
        }
    };

    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'unapproved', label: 'Unapproved' },
        { value: 'expired', label: 'Expired' },
        { value: 'PendingPayment', label: 'Awaiting payment (new members)' }
    ];

    const fetchUsers = useCallback(async (page = 1, search = '') => {
        setLoading(true);

        try {
            const params = {
                page: page,
                limit: pagination.perPage,
                paginate: pagination.isPaginated ? 'true' : 'false',
                status: status === 'all' ? null : status
            };

            if (search.trim() !== '') {
                params.search = search.trim();
            }

            const queryParams = new URLSearchParams(
                Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null))
            ).toString();

            const response = await fetch(`${API_URL}/users?${queryParams}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();

                if (pagination.isPaginated) {
                    setUsers(data.data);
                    setPagination(prev => ({
                        ...prev,
                        currentPage: data.current_page,
                        total: data.total,
                        lastPage: data.last_page
                    }));
                } else {
                    setUsers(data);
                    setPagination(prev => ({
                        ...prev,
                        currentPage: 1,
                        total: data.length,
                        lastPage: 1
                    }));
                }
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch users");
            }
        } catch (error) {
            setError("An error occurred while fetching users.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [navigate, status, pagination.perPage, pagination.isPaginated, token, location]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        const timer = setTimeout(() => {
            fetchUsers(1, value);
        }, 500);
        return () => clearTimeout(timer);
    };

    const handlePageChange = (page) => {
        fetchUsers(page, searchTerm);
    };

    const handlePerPageChange = (e) => {
        const newPerPage = parseInt(e.target.value);
        setPagination(prev => ({
            ...prev,
            perPage: newPerPage,
            currentPage: 1
        }));
    };

    const togglePagination = () => {
        setPagination(prev => ({
            ...prev,
            isPaginated: !prev.isPaginated,
            currentPage: 1
        }));
    };

    const handleStatusChange = (newStatus) => {
        navigate(`/dashboard/members/${newStatus}`);
    };

    // Before asking, find out who would be affected, so the dialog can say so.
    const handleExpireAllClick = async () => {
        setExpiryPreview(null);
        setIsConfirmDialogOpen(true);
        try {
            const response = await fetch(`${API_URL}/users/expire/preview`, {
                headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
            });
            if (response.ok) setExpiryPreview(await response.json());
        } catch (error) {
            // The dialog still works without the numbers.
        }
    };

    const expiryMessage = expiryPreview
        ? `Are you sure you want to expire the accounts for ${expiryPreview.year}?`
        : "Are you sure you want to expire the accounts?";

    const expiryNote = expiryPreview
        ? [
            `${expiryPreview.to_expire} member(s) who joined before ${expiryPreview.year} will be expired` +
                (expiryPreview.admins_to_expire > 0 ? `, including ${expiryPreview.admins_to_expire} admin(s)` : "") +
                ", and must pay the membership fee to renew.",
            `${expiryPreview.new_members} member(s) who joined in ${expiryPreview.year} will be set to "Awaiting payment" and asked to pay to complete their membership.`,
            `${expiryPreview.still_expired} member(s) still expired from an earlier year will be treated as expired today and reminded to renew.`,
            `${expiryPreview.unconfirmed} sign-up(s) that never confirmed their email will get a reminder with a new confirmation link.`,
            "Everyone affected gets an email, and an SMS where a phone number is known.",
            expiryPreview.you_will_be_expired ? "Your own account is included: you will need to pay to renew it the next time you sign in." : "",
        ].filter(Boolean).join(" ")
        : "Members who joined before this year will be expired, members who joined this year will be asked to pay to complete their membership, and unconfirmed sign-ups will be reminded. Each gets an email and an SMS.";

    const confirmExpireAllAccounts = async () => {
        setIsExpiringUsers(true);
        try {
            const response = await fetch(`${API_URL}/users/expire`, {
                method: 'PATCH',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                setNotification({ type: "success", message: data.message || 'Accounts expired successfully' });
                navigate('/dashboard/members/expired');

            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: 'Users not found.' });
            } else {
                setNotification({ type: "danger", message: 'There was an error.' });
                throw new Error('Failed to expire accounts');
            }
        } catch (error) {
            setNotification({ message: 'An error occurred while expiring accounts.', type: 'danger' });
            console.error("Error:", error);
        } finally {
            setIsConfirmDialogOpen(false);
            setIsExpiringUsers(false);
        }
    };

    const cancelExpireAllAccounts = () => {
        setIsConfirmDialogOpen(false);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Members</p>
                        </div>
                        {isAdmin(userRole) && (
                            <div className="level-item">
                                <div className="select">
                                    <select
                                        value={status}
                                        onChange={(e) => handleStatusChange(e.target.value)}
                                        className="ml-3"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="level-right">
                        <div className="level-item">
                            {canDeactivateAllAccounts(userRole) && (
                                <button className="button is-danger is-small is-bold" onClick={() => handleExpireAllClick()}>
                                    <span className="icon">
                                        <i className="fas fa-toggle-off"></i>
                                    </span> &nbsp; &nbsp; Expire Accounts
                                </button>
                            )}
                        </div>

                        <div className="level-item">
                            <label className="checkbox mr-3">
                                <input
                                    type="checkbox"
                                    checked={pagination.isPaginated}
                                    onChange={togglePagination}
                                />{' '}
                                Paginate
                            </label>
                        </div>
                        {pagination.isPaginated && (
                            <div className="level-item">
                                <div className="select">
                                    <select
                                        value={pagination.perPage}
                                        onChange={handlePerPageChange}
                                    >
                                        <option value="10">10 per page</option>
                                        <option value="15">15 per page</option>
                                        <option value="30">30 per page</option>
                                        <option value="50">50 per page</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </nav>

                <ConfirmDialog
                    isOpen={isConfirmDialogOpen}
                    theme="is-danger"
                    title="Expire Accounts"
                    message={expiryMessage}
                    note={expiryNote}
                    onConfirm={confirmExpireAllAccounts}
                    onCancel={cancelExpireAllAccounts}
                    isLoading={isExpiringUsers}
                />

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <div className="field">
                    <div className="control has-icons-left">
                        <input
                            className="input"
                            type="text"
                            placeholder="Search by name, email, or role"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <span className="icon is-left">
                            <i className="fas fa-search" />
                        </span>
                    </div>
                </div>

                {loading ? (
                    <div className="has-text-centered py-5">
                        <span className="icon is-large">
                            <i className="fas fa-spinner fa-pulse fa-2x" />
                        </span>
                        <p>Loading users...</p>
                    </div>
                ) : error ? (
                    <div className="notification is-danger">{error}</div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="table is-striped is-fullwidth is-hoverable">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone #</th>
                                        <th>Role</th>
                                        <th>Created At</th>
                                        {showLastLogin && <th>Last Login</th>}
                                        <th>Options</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length > 0 ? (
                                        users.map((user, index) => (
                                            <tr key={user.id}>
                                                <td>{pagination.isPaginated
                                                    ? (pagination.currentPage - 1) * pagination.perPage + index + 1
                                                    : index + 1}
                                                </td>
                                                <td>

                                                    <Link to={`/dashboard/members/${user.id}/details`} className="primary-text-color">
                                                        {user.first_name} {user.last_name}
                                                    </Link> <sup>{user.status != 'Active' && (
                                                        <span className="tag is-danger is-light is-hoverable"><small> {user?.status} </small> </span>)}</sup>
                                                </td>
                                                <td>{user?.email}</td>
                                                <td>{user?.phone_number || "N/A"}</td>
                                                <td>{user?.role?.name}</td>
                                                <td>{formatTimestamp(user.created_at)}</td>
                                                {showLastLogin && <td>{user.last_login_at ? formatTimestamp(user.last_login_at) : "Never"}</td>}
                                                <td>
                                                    <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                        <div className="dropdown-trigger">
                                                            <button
                                                                className="button is-white"
                                                                aria-haspopup="true"
                                                                aria-controls="dropdown-menu"
                                                                onClick={() => toggleDropdown(index)}
                                                            >
                                                                <span className="icon">
                                                                    <i className="fas fa-ellipsis-v"></i>
                                                                </span>
                                                            </button>
                                                        </div>
                                                        <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                            <div className="dropdown-content">
                                                                <Link to={`/dashboard/members/${user.id}/details`} className="dropdown-item">
                                                                    <span className="icon">
                                                                        <i className="fas fa-eye"></i>
                                                                    </span>
                                                                    <span>View</span>
                                                                </Link>
                                                                <Link to="#" className="dropdown-item">
                                                                    <span className="icon">
                                                                        <i className="fas fa-trash-alt"></i>
                                                                    </span>
                                                                    <span>De-activate</span>
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={showLastLogin ? 8 : 7} className="has-text-centered">No users found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {pagination.isPaginated && pagination.lastPage > 1 && (
                            <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                                <button
                                    className="pagination-previous"
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                >
                                    Previous
                                </button>
                                <button
                                    className="pagination-next"
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.lastPage}
                                >
                                    Next
                                </button>
                                <ul className="pagination-list">
                                    {Array.from({ length: Math.min(5, pagination.lastPage) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.lastPage <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                                            pageNum = pagination.lastPage - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <li key={pageNum}>
                                                <button
                                                    className={`pagination-link ${pagination.currentPage === pageNum ? 'is-current' : ''}`}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                    {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                                        <>
                                            <li><span className="pagination-ellipsis">&hellip;</span></li>
                                            <li>
                                                <button
                                                    className="pagination-link"
                                                    onClick={() => handlePageChange(pagination.lastPage)}
                                                >
                                                    {pagination.lastPage}
                                                </button>
                                            </li>
                                        </>
                                    )}
                                </ul>
                            </nav>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

export default Users;